import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmActionDialog } from '../../../components/design-system/admin';

/** A form field; `label`, `placeholder` and the option labels are i18n keys. */
export interface CrmField {
  key: string;
  label: string;
  type?: 'text' | 'date' | 'select' | 'textarea' | 'country';
  options?: Record<string, string>;
  placeholder?: string;
  /** Takes the whole row instead of half. */
  full?: boolean;
}

interface CrmRecordSheetProps<T> {
  /** The record being edited, or a partial seed for a new one; null closes the sheet. */
  record: Partial<T> | null;
  fields: CrmField[];
  title: { create: string; edit: string };
  /** The field a record cannot be saved without (e.g. company, email). */
  required: string;
  onSave: (data: Partial<T>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
  /** Shown under the fields, for what isn't a plain field (e.g. a lead's vendor). */
  extra?: React.ReactNode;
}

/**
 * One edit form for every CRM record type, in a right-hand sheet (the same
 * pattern as the quote-request detail). The field list drives the layout.
 */
export function CrmRecordSheet<T extends { _id?: string }>({
  record, fields, title, required, onSave, onDelete, onClose, extra,
}: CrmRecordSheetProps<T>) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!record) return;
    const seeded: Record<string, string> = {};
    fields.forEach((f) => { seeded[f.key] = String((record as Record<string, unknown>)[f.key] ?? ''); });
    setForm(seeded);
  }, [record, fields]);

  const id = record?._id;
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const missing = !form[required]?.trim();

  const save = async () => {
    if (missing) return;
    setSaving(true);
    try {
      await onSave({ ...(id ? { _id: id } : {}), ...form } as Partial<T>);
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (f: CrmField) => {
    const value = form[f.key] ?? '';
    const placeholder = f.placeholder ? t(f.placeholder) : undefined;
    switch (f.type) {
      case 'textarea':
        return <Textarea id={`crm-${f.key}`} rows={7} value={value} onChange={set(f.key)} placeholder={placeholder} />;
      case 'select':
        return (
          <NativeSelect id={`crm-${f.key}`} className="w-full" value={value} onChange={set(f.key)}>
            {Object.entries(f.options || {}).map(([k, v]) => <NativeSelectOption key={k} value={k}>{t(v)}</NativeSelectOption>)}
          </NativeSelect>
        );
      case 'date':
        return <Input id={`crm-${f.key}`} type="date" value={value} onChange={set(f.key)} />;
      case 'country':
        return <Input id={`crm-${f.key}`} value={value} onChange={set(f.key)} maxLength={2} className="uppercase" placeholder="FR" />;
      default:
        return <Input id={`crm-${f.key}`} value={value} onChange={set(f.key)} placeholder={placeholder} />;
    }
  };

  return (
    <>
      <Sheet open={!!record} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-full overflow-y-auto p-6 sm:max-w-[520px]">
          <SheetHeader className="mb-4 p-0">
            <SheetTitle className="font-heading text-xl font-medium tracking-tight">{id ? title.edit : title.create}</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-x-3 gap-y-4">
            {fields.map((f) => (
              <div key={f.key} className={cn('flex flex-col gap-1.5', (f.full || f.type === 'textarea') && 'col-span-2')}>
                <Label htmlFor={`crm-${f.key}`} className="text-xs text-muted-foreground">
                  {t(f.label)}{f.key === required && ' *'}
                </Label>
                {renderInput(f)}
              </div>
            ))}
          </div>
          {extra && <div className="mt-4">{extra}</div>}
          <div className="mt-6 flex items-center gap-2">
            {id && (
              <Button variant="outline" className="text-destructive" onClick={() => setConfirmDelete(true)}>{t('ADMIN_CRM.COMMON.DELETE')}</Button>
            )}
            <span className="flex-1" />
            <Button variant="ghost" onClick={onClose}>{t('ADMIN_CRM.COMMON.CANCEL')}</Button>
            <Button onClick={save} disabled={saving || missing}>{t(saving ? 'ADMIN_CRM.COMMON.SAVING' : 'ADMIN_CRM.COMMON.SAVE')}</Button>
          </div>
        </SheetContent>
      </Sheet>
      <ConfirmActionDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('ADMIN_CRM.COMMON.DELETE_TITLE')}
        description={t('ADMIN_CRM.COMMON.DELETE_DESCRIPTION')}
        confirmLabel={t('ADMIN_CRM.COMMON.DELETE')}
        destructive
        onConfirm={async () => {
          if (id) await onDelete(id);
          setConfirmDelete(false);
        }}
      />
    </>
  );
}
